import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import { useOrg } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { fmtDateTime, fmtTime, KIND_LABEL, mealName, passChip, seats, serveDateText, windowText, eventTitle } from '../lib/format';
import { qrValue } from '../lib/qr';
import { canShareFiles, download, fileName, shareFile } from '../lib/share';
import type { Pass, SeatPass, Via } from '../lib/types';
import type { ArtFlat } from '../lib/passArt';
import { Chip, ErrorNote, Pips, QrCanvas, Spinner, VegMark } from './ui';

export type { ArtFlat };

export const passQrValue = (p: Pick<Pass, 'id'>) => qrValue.meal(p.id);

export const VIA_LABEL: Record<Via, string> = { flat: 'Family pass', meal: 'Meal pass', seat: 'Single-seat pass' };

/** Who is looking at the passes, and how to refresh them after a seat is kept or taken back. */
export interface PassScope {
  flat: ArtFlat;
  /** The passes link token, when not signed in. */
  token?: string;
  /** Wording for "issue new QR codes" in the privacy note. */
  reissueHint: string;
  onChanged: () => void;
}

const art = () => import('../lib/passArt');

/** Draws a share image (or anything else) in the background; null until ready. */
function useArtBlob(make: (() => Promise<Blob>) | null, key: string) {
  const [state, setState] = useState<{ key: string; blob: Blob | null; failed: boolean }>({ key: '', blob: null, failed: false });
  useEffect(() => {
    if (!make) return;
    let live = true;
    make().then((blob) => live && setState({ key, blob, failed: false }), () => live && setState({ key, blob: null, failed: true }));
    return () => { live = false; };
  }, [key]); // make() changes every render; key says when the picture changes
  return state.key === key ? state : { blob: null, failed: false };
}

function useObjectUrl(blob: Blob | null) {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

export function Modal({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="qr-modal" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div className="qr-modal-card" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

/** "Share on WhatsApp" (the phone's share sheet) where the phone supports it, and "Download image". */
export function ImageButtons({ blob, failed, filename, onDone }: {
  blob: Blob | null; failed?: boolean; filename: string; onDone?: (how: 'shared' | 'saved') => void;
}) {
  const share = canShareFiles();
  if (failed) return <p className="err">Couldn't draw the image. Take a screenshot of the QR instead.</p>;
  return (
    <div className="share-row">
      {share && (
        <button type="button" className="btn primary sm" disabled={!blob} onClick={() => {
          if (blob) void shareFile(blob, filename).then((r) => { if (r !== 'cancelled') onDone?.(r); });
        }}>{blob ? 'Share on WhatsApp' : 'Preparing…'}</button>
      )}
      <button type="button" className="btn ghost sm" disabled={!blob} onClick={() => {
        if (!blob) return;
        download(blob, filename);
        onDone?.('saved');
      }}>{blob || share ? 'Download image' : 'Preparing…'}</button>
    </div>
  );
}

const venueOf = (pass: Pass, kind: Pass['lines'][number]['kind']) => pass.venues?.find((v) => v.kind === kind)?.name;

/** "T6 Banquet", or "T6 Banquet & T7 Banquet" when the flat's kinds of this meal are served in different halls. */
export function passVenueText(pass: Pass): string {
  const kinds = new Set([...pass.lines.filter((l) => l.booked_adults + l.booked_children > 0).map((l) => l.kind), ...pass.pending.map((p) => p.kind)]);
  return [...new Set((pass.venues ?? []).filter((v) => kinds.has(v.kind)).map((v) => v.name))].join(' & ');
}

/** Seats per kind with pips: served, kept for a guest, and left. Locked passes show pending seats. */
export function SeatsView({ pass }: { pass: Pass }) {
  const locked = pass.state === 'locked';
  const rows = locked
    ? pass.pending.map((p) => ({ kind: p.kind, a: p.adults, c: p.children, sa: 0, sc: 0, ga: 0, gc: 0 }))
    : pass.lines.map((l) => ({
      kind: l.kind, a: l.booked_adults, c: l.booked_children, sa: l.served_adults, sc: l.served_children,
      ga: l.given_adults ?? 0, gc: l.given_children ?? 0,
    }));
  const row = (label: string, total: number, served: number, given: number) => (
    <div className="seat-row"><span className="seat-lbl">{label}</span><Pips total={total} used={served} given={given} locked={locked} />
      <span className="seat-left">{locked ? `${total} booked` : `${Math.max(total - served - given, 0)} of ${total} left${given ? ` · ${given} kept` : ''}`}</span></div>
  );
  return (
    <div className="seats">
      {rows.map((r) => (
        <div className="seat-kind" key={r.kind}>
          <p className="seat-k"><VegMark kind={r.kind} />{KIND_LABEL[r.kind]}{venueOf(pass, r.kind) ? <span className="seat-venue"> · {venueOf(pass, r.kind)}</span> : null}</p>
          {r.a > 0 && row('Adult', r.a, r.sa, r.ga)}
          {r.c > 0 && row('Child', r.c, r.sc, r.gc)}
        </div>
      ))}
      {!locked && pass.given > 0 && <p className="fine">Kept seats are for guests' single-seat passes. Your flat and meal QR codes don't use them.</p>}
    </div>
  );
}

export function ServedHistory({ pass }: { pass: Pass }) {
  if (!pass.history.length) return null;
  return (
    <div className="hist stack">
      <p className="app-h">Served so far</p>
      <ul>{pass.history.map((h) => (
        <li key={h.request_key + h.kind}>
          <span>{KIND_LABEL[h.kind]} · {seats(h.adults, h.children)}</span>
          <span>{fmtTime(h.at)} · {h.counter}{h.via ? ` · ${VIA_LABEL[h.via]}` : ''}</span>
        </li>
      ))}</ul>
    </div>
  );
}

const confirmedPasses = (passes: Pass[]) => passes.filter((p) => p.booked > 0 && p.state !== 'void');
const passesKey = (passes: Pass[]) => passes.map((p) => `${p.id}:${p.booked}:${p.served}:${p.given}:${p.state}`).join('|');

/** The family pass in a list: one QR for every meal. */
export function FlatPassCard({ scope, passes, onOpen }: { scope: PassScope; passes: Pass[]; onOpen: () => void }) {
  const meals = confirmedPasses(passes).filter((p) => p.state === 'ready' || p.state === 'partly_served').length;
  return (
    <button type="button" className="fpass" onClick={onOpen}>
      <span className="fpass-qr"><QrCanvas value={qrValue.flat(scope.flat.passToken)} size={200} /></span>
      <span className="fpass-main">
        <span className="fpass-eyebrow">Family pass · every meal</span>
        <span className="fpass-title">One QR for {eventTitle(passes) === 'Food passes' ? 'every meal' : eventTitle(passes)}</span>
        <span className="fpass-sub">{meals ? `Works at ${meals} booked meal${meals === 1 ? '' : 's'}, and any you book later` : 'Works at every meal you book'}</span>
      </span>
      <span className="chev" aria-hidden="true">›</span>
    </button>
  );
}

export function FlatPassModal({ scope, passes, onClose }: { scope: PassScope; passes: Pass[]; onClose: () => void }) {
  const open = confirmedPasses(passes).filter((p) => p.state === 'ready' || p.state === 'partly_served');
  const img = useArtBlob(() => art().then((a) => a.flatPassImage(scope.flat, passes)), `flat:${scope.flat.passToken}:${passesKey(passes)}`);
  return (
    <Modal label={`Family pass for ${scope.flat.label}`} onClose={onClose}>
      <p className="app-eyebrow" style={{ color: '#6b5a4c' }}>Family pass · every meal</p>
      <p className="pass-title" style={{ fontSize: 26 }}>Flat {scope.flat.label}</p>
      <p style={{ fontSize: 13 }}>{scope.flat.name}</p>
      <QrCanvas value={qrValue.flat(scope.flat.passToken)} />
      <p className="fine" style={{ color: '#5b4a3e' }}>
        Show it at any meal: the counter finds what your flat booked for the meal being served. Meals you book later are added by themselves.
      </p>
      {open.length > 0 && (
        <ul className="fpass-list">
          {open.map((p) => (
            <li key={p.id}>
              <span><b>{p.sitting.day_label} · {mealName(p.sitting)}</b><small>{serveDateText(p.sitting)} · {windowText(p.sitting)}</small></span>
              <span>{p.lines.filter((l) => l.left_adults + l.left_children > 0).map((l) => (
                <small key={l.kind}><VegMark kind={l.kind} />{seats(l.left_adults, l.left_children)}</small>
              ))}</span>
            </li>
          ))}
        </ul>
      )}
      <ImageButtons blob={img.blob} failed={img.failed} filename={fileName('ONE10', 'family-pass', scope.flat.label) + '.png'} />
      <p className="fine" style={{ color: '#5b4a3e' }}>
        Keep it within the family: it works for every meal. For a guest, share a single-seat pass instead. If it reaches the wrong people, {scope.reissueHint}.
      </p>
      <button className="btn ghost" onClick={onClose}>Close</button>
    </Modal>
  );
}

/** Large meal QR over the page (passes link and the committee's flat page), with sharing and single-seat passes. */
export function MealPassModal({ pass, scope, onClose }: { pass: Pass; scope: PassScope; onClose: () => void }) {
  const s = pass.sitting;
  const chip = passChip(pass);
  const locked = pass.state === 'locked';
  return (
    <Modal label={`${s.day_label} ${mealName(s)} pass`} onClose={onClose}>
      <p className="app-eyebrow" style={{ color: '#6b5a4c' }}>{s.day_label_bn} · {s.day_label} · {serveDateText(s)}</p>
      <p className="pass-title" style={{ fontSize: 24 }}>{mealName(s)}</p>
      <p style={{ fontSize: 13 }}>{windowText(s)}{passVenueText(pass) ? ` · ${passVenueText(pass)}` : ''} · Flat {pass.holder.label} · <Chip tone={chip.tone}>{chip.label}</Chip></p>
      <QrCanvas value={passQrValue(pass)} />
      {locked && <p className="err">Payment not confirmed yet ({pass.blocking_order?.code}). The counter will send you to the help desk.</p>}
      <SeatsView pass={pass} />
      {!locked && <MealShareButtons pass={pass} scope={scope} />}
      <SeatList pass={pass} scope={scope} />
      <button className="btn ghost" onClick={onClose}>Close</button>
    </Modal>
  );
}

/** Share or download the meal pass as an image in the site's style. */
export function MealShareButtons({ pass, scope }: { pass: Pass; scope: PassScope }) {
  const s = pass.sitting;
  const img = useArtBlob(() => art().then((a) => a.mealPassImage(scope.flat, pass)), `meal:${passesKey([pass])}`);
  return <ImageButtons blob={img.blob} failed={img.failed} filename={fileName('ONE10', s.day_label, mealName(s), scope.flat.label) + '.png'} />;
}

const SEAT_CHIP: Record<SeatPass['state'], { label: string; tone: '' | 'ok' | 'warn' }> = {
  ready: { label: 'Share', tone: 'ok' },
  given: { label: 'Kept for a guest', tone: 'warn' },
  served: { label: 'Served', tone: '' },
};

/** One QR per seat, for a guest who comes on their own. */
export function SeatList({ pass, scope }: { pass: Pass; scope: PassScope }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const list = pass.seats ?? [];
  if (!list.length || (pass.state !== 'ready' && pass.state !== 'partly_served')) return null;
  const open = list.find((x) => x.id === openId) ?? null;
  return (
    <section className="seatlist">
      <p className="app-h">Single-seat passes for guests</p>
      <p className="fine">One QR per seat. Sharing one keeps that seat for your guest: your flat and meal QR codes skip it until you take it back.</p>
      <ul>
        {list.map((x) => (
          <li key={x.id}>
            <button type="button" className="seat-btn" disabled={x.state === 'served'} onClick={() => setOpenId(x.id)}>
              <span><VegMark kind={x.kind} />{KIND_LABEL[x.kind]} · {x.child ? 'Child' : 'Adult'} {x.no}</span>
              <Chip tone={SEAT_CHIP[x.state].tone}>{SEAT_CHIP[x.state].label}</Chip>
            </button>
          </li>
        ))}
      </ul>
      {open && <SeatModal pass={pass} seat={open} scope={scope} onClose={() => setOpenId(null)} />}
    </section>
  );
}

function SeatModal({ pass, seat, scope, onClose }: { pass: Pass; seat: SeatPass; scope: PassScope; onClose: () => void }) {
  const s = pass.sitting;
  const img = useArtBlob(() => art().then((a) => a.seatPassImage(scope.flat, pass, seat)), `seat:${seat.id}`);
  const url = useObjectUrl(img.blob);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function keep(on: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.giveSeat(seat.id, on, scope.token);
      scope.onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal label="Single-seat pass" onClose={onClose}>
      <p className="app-eyebrow" style={{ color: '#6b5a4c' }}>Single-seat pass · one plate</p>
      <p className="pass-title" style={{ fontSize: 22 }}>{s.day_label} {mealName(s)}</p>
      <p style={{ fontSize: 13 }}><VegMark kind={seat.kind} />{KIND_LABEL[seat.kind]} · {seat.child ? 'Child' : 'Adult'} · seat {seat.no} of {seat.of}</p>
      {url ? <img className="seat-art" src={url} alt={`Guest pass for ${s.day_label} ${mealName(s)}`} /> : img.failed ? null : <div className="empty"><Spinner /></div>}
      {seat.state === 'given' ? (
        <div className="notice ok" style={{ textAlign: 'left' }}>
          <p><b>Kept for a guest</b>{seat.given_at ? ` since ${fmtDateTime(seat.given_at)}` : ''}. Your flat and meal QR codes won't use this seat.</p>
          <button className="btn ghost sm" style={{ justifySelf: 'start' }} disabled={busy} onClick={() => void keep(false)}>Take it back</button>
        </div>
      ) : (
        <p className="fine" style={{ color: '#5b4a3e' }}>
          Sharing or downloading this pass keeps the seat for your guest. It works once, at this meal only.
        </p>
      )}
      <ImageButtons blob={img.blob} failed={img.failed}
        filename={fileName('ONE10', 'guest-pass', s.day_label, mealName(s), KIND_LABEL[seat.kind], seat.child ? 'child' : 'adult', String(seat.no), scope.flat.label) + '.png'}
        onDone={() => { if (seat.state === 'ready') void keep(true); }} />
      <ErrorNote error={error} />
      <button className="btn ghost" onClick={onClose}>Close</button>
    </Modal>
  );
}

/** "Download all passes (PDF)": page 1 the family pass, a page per day, then the single-seat passes. */
export function PdfButton({ scope, passes, block }: { scope: PassScope; passes: Pass[]; block?: boolean }) {
  const key = `${scope.flat.passToken}:${passesKey(passes)}`;
  const [made, setMade] = useState<{ key: string; blob: Blob } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const share = canShareFiles();
  const name = fileName('ONE10', eventTitle(passes), 'food-passes', scope.flat.label) + '.pdf';
  const blob = made?.key === key ? made.blob : null;
  const any = passes.some((p) => p.booked > 0 && (p.state === 'ready' || p.state === 'partly_served'));
  const cls = `btn ghost${block ? ' block' : ' sm'}`;
  const issuer = useOrg().data?.payee_name;

  async function make() {
    setBusy(true);
    setError(null);
    try {
      const b = await (await art()).passesPdf({ ...scope.flat, issuer }, passes);
      setMade({ key, blob: b });
      if (!share) download(b, name);
    } catch {
      setError("Couldn't make the PDF on this phone. Take screenshots of the QR codes instead.");
    } finally {
      setBusy(false);
    }
  }

  if (blob && share) {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <div className="share-row">
          <button type="button" className="btn primary sm" onClick={() => void shareFile(blob, name)}>Share PDF on WhatsApp</button>
          <button type="button" className="btn ghost sm" onClick={() => download(blob, name)}>Save PDF</button>
        </div>
        <p className="fine">PDF ready: the family pass, a page for each day and the single-seat passes.</p>
      </div>
    );
  }
  return (
    <>
      <button type="button" className={cls} disabled={!any || busy} onClick={() => (blob ? download(blob, name) : void make())}>
        {busy ? 'Preparing PDF…' : 'Download all passes (PDF)'}
      </button>
      <ErrorNote error={error} />
    </>
  );
}
